import type { Field, Form } from "@/models/new/forms.types"
import type { FormResponse } from "@/models/new/formResponse.types"
import { getDynamicGroupInstances } from "../shared/attachments/attachment-groups"

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const findQuestion = (
  form: Form,
  key: string,
  responses: FormResponse["responses"],
  resolvedInstancesByGroup?: Record<string, number[]>,
  fieldMedia?: FormResponse["fieldMedia"],
  pendingMediaLocators?: string[],
): { label: string; sectionLabel: string; sectionId: string; field: Field } | undefined => {
  for (const section of form.fields) {
    for (const field of section.fields) {
      if (field.id === key)
        return { label: field.label, sectionLabel: section.label, sectionId: section.id, field }
      if (field.type !== "dynamicGroup") continue
      const match = /^(.+)\.([1-9]\d*)\.([^.]+)$/.exec(key)
      if (match?.[1] !== field.id) continue
      const child = field.fields.find((candidate) => candidate.name === match[3])
      if (child) {
        // The accordion (DynamicGroup.tsx) labels rows by the positional index of an
        // instance in `responses ∪ fieldMedia ∪ createdInstances` (sorted). `createdInstances`
        // tracks added-but-unfilled datepicker/geolocation-only rows that have no `responses`
        // key, and `fieldMedia` carries media-only rows with no matching `responses` key, so
        // a `responses`-only enumeration shifts every later row's number when either precedes
        // the rejected row. The editor lifts the resolved set and forwards it here; when the
        // lift is absent (a never-opened or unmounted group), include persisted and pending
        // media. Only ephemeral, unfilled UI rows disappear when a group unmounts.
        const resolved =
          resolvedInstancesByGroup?.[field.id] ??
          getDynamicGroupInstances(responses, field.id, fieldMedia, pendingMediaLocators)
        const position = resolved.indexOf(Number(match[2])) + 1
        const suffix = position > 0 ? ` #${position}` : ""
        return {
          label: `${field.label}${suffix} — ${child.label}`,
          sectionLabel: section.label,
          sectionId: section.id,
          field: child,
        }
      }
    }
  }
  return undefined
}

/** Translate API submission issues and Zod update/multipart issues without exposing answer values. */
export const reportValidationErrorMessage = (
  data: unknown,
  form: Form,
  responses: FormResponse["responses"],
  fallback = "The report could not be saved. Please try again.",
  resolvedInstancesByGroup?: Record<string, number[]>,
  fieldMedia?: FormResponse["fieldMedia"],
  pendingMediaLocators?: string[],
): string => {
  const error = asRecord(data)
  const details = asRecord(error.details)
  let issues: unknown = details.issues ?? error.errors

  // Older JSON/multipart boundaries encode Zod issues in the message instead of details.
  if (!Array.isArray(issues) && typeof error.message === "string") {
    try {
      const parsed: unknown = JSON.parse(error.message)
      if (Array.isArray(parsed)) issues = parsed
    } catch {
      // A normal API message is not JSON.
    }
  }

  if (!Array.isArray(issues)) {
    if (error.code === "VALIDATION_ERROR" || error.message === "Validation error") {
      return "Some report information could not be validated. Review the required questions and attachments. If it still fails, contact support with the report name and time of the attempt."
    }
    return typeof error.message === "string" && error.message.trim() ? error.message : fallback
  }

  type Entry = {
    dedupKey: string
    sectionId: string | undefined
    sectionLabel: string | undefined
    message: string
  }
  const entries: Entry[] = issues.map((candidate): Entry => {
    const issue = asRecord(candidate)
    const path = Array.isArray(issue.path) ? issue.path : []
    const responseIndex = path.findIndex((part) => part === "responses" || part === "fieldMedia")
    const key = issue.responseKey ?? (responseIndex >= 0 ? path[responseIndex + 1] : undefined)
    const question =
      typeof key === "string"
        ? findQuestion(
            form,
            key,
            responses,
            resolvedInstancesByGroup,
            fieldMedia,
            pendingMediaLocators,
          )
        : undefined

    // unknown-response renders an intentionally key-independent message; dedup it by message so
    // two salvaged unknown responses don't double-toast.
    if (issue.code === "unknown-response") {
      const message =
        "A saved answer no longer belongs to this form. Contact support before discarding your changes."
      return {
        dedupKey: `__unknown__::${message}`,
        sectionId: undefined,
        sectionLabel: undefined,
        message,
      }
    }

    const label =
      question?.label ??
      (path.includes("organizationEntityId") ? "Reporting organization" : "Report information")
    let guidance: string
    if (issue.code === "required-response") {
      guidance = "An answer is required."
    } else if (path.includes("attachments") || question?.field.type === "file") {
      guidance = "Check the attachment. Remove and re-add it if it cannot be saved."
    } else if (
      path.includes("note") &&
      issue.code === "too_big" &&
      typeof issue.maximum === "number"
    ) {
      guidance = `Keep the note to ${issue.maximum} characters or fewer.`
    } else {
      switch (question?.field.type) {
        case "datepicker":
          guidance = "Enter a valid date."
          break
        case "select":
        case "boolean":
        case "organization":
          guidance = "Choose a valid option."
          break
        case "geolocation":
          guidance = "Provide a valid location."
          break
        case "signature":
          guidance = "Check the signature and add it again if needed."
          break
        default:
          guidance = "Review this answer and correct its format."
      }
    }
    const message = `${label}: ${guidance}`
    // Dedup by issue identity (responseKey/path-embedded key + rendered message) so distinct
    // questions sharing (label, guidance) both survive, while exact server re-emissions collapse.
    const dedupKey =
      typeof key === "string" ? `${key}::${message}` : `${message}::${path.join(".")}`
    return {
      dedupKey,
      sectionId: question?.sectionId,
      sectionLabel: question?.sectionLabel,
      message,
    }
  })

  // Dedup by identity; exact duplicates (same key + message) collapse, matching the old
  // Set-on-rendered-string behaviour for that case.
  const seen = new Set<string>()
  const survivors = entries.filter((entry) => {
    if (seen.has(entry.dedupKey)) return false
    seen.add(entry.dedupKey)
    return true
  })

  // Disambiguate messages shared by more than one surviving question by appending the enclosing
  // section label, so the user can locate each offending field across sections and fix both in
  // one pass. Solo messages stay unchanged; identical rendered lines are collapsed below.
  // Section labels are free text with no uniqueness constraint (Section.label: string), so when
  // multiple sections share a label the `(in <label>)` suffix alone is not enough to keep two
  // distinct cross-section errors apart — the final Set would collapse them into one line and
  // silently drop an error. Append the section's 1-based positional index when its label is
  // shared so each line stays locatable and survives the collapse. Within-section duplicates
  // share the same section id (hence the same ordinal) and still collapse as before.
  const messageCounts = new Map<string, number>()
  for (const entry of survivors) {
    messageCounts.set(entry.message, (messageCounts.get(entry.message) ?? 0) + 1)
  }
  const sectionLabelCounts = new Map<string, number>()
  for (const section of form.fields) {
    const lbl = section.label ?? ""
    sectionLabelCounts.set(lbl, (sectionLabelCounts.get(lbl) ?? 0) + 1)
  }
  const messages = survivors.map((entry) => {
    if ((messageCounts.get(entry.message) ?? 0) > 1 && entry.sectionId) {
      const sharedLabel = (sectionLabelCounts.get(entry.sectionLabel ?? "") ?? 0) > 1
      if (sharedLabel) {
        const sectionIndex = form.fields.findIndex((s) => s.id === entry.sectionId) + 1
        return entry.sectionLabel
          ? `${entry.message} (in ${entry.sectionLabel} — section ${sectionIndex})`
          : `${entry.message} (in section ${sectionIndex})`
      }
      return entry.sectionLabel ? `${entry.message} (in ${entry.sectionLabel})` : entry.message
    }
    return entry.message
  })

  return messages.length
    ? `Please correct the following: ${[...new Set(messages)].join(" ")}`
    : "Some report information could not be validated. Review the required questions and attachments, or contact support if the problem continues."
}
